import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  TextField,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  getOrdersById,
  getSalesReturnReason,
  createSalesReturn,
} from "../../app/services/OrdersService";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";

interface Order {
  id: number;
  unique_id: string;
  total_amount: string;
  created_at: string;
  user: {
    name: string;
    email: string;
    mobile: string;
  };
  items: {
    product_id: number;
    quantity: number;
    price: string;
    total: string;
    updatedQuantity?: { quantity: number }[];
    product: {
      name: string;
      sku: string;
      packaging_type: string;
      base_unit: string;
      base_mrp: string;
      quantity: string;
      tax?: string;
    };
  }[];
  salesReturns?: {
    id: number;
    notes: string;
    created_at: string;
    //history: {
      //id: number;
      //total_quantity: number;
      items: {
        id: number;
        quantity: number;
        product_id: number;
        created_at: string;
        user: {
          id: number;
          name: string;
          email: string;
          mobile: string;
        };
        reason: {
          id: number;
          name: string;
        };
        product: {
          id: number;
          name: string;
          sku: string;
          quantity: number;
          unit: string;
          base_price: string;
        };
        images: any[];
      }[];
    };
 // };
}

interface SelectedProduct {
  product_id: number;
  name: string;
  quantity: number;
  maxQuantity: number;
  reasonId?: number | "";
  files?: File[];
}

interface Reason {
  id: number;
  name: string;
}

const CreateSalesReturn: React.FC = () => {
  const { id } = useParams();
  const [orderData, setOrderData] = useState<Order | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<number | "">("");
  const [reasonList, setReasonList] = useState<Reason[]>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        if (!id) return;
        const res = await getOrdersById(Number(id));
        if (res.status === "success") {
          setOrderData(res.data);
          if (res.data.salesReturns?.notes) setNotes(res.data.salesReturns.notes);
        } else {
          toast.error("Failed to fetch order");
        }
      } catch (error) {
        toast.error("Failed to fetch order");
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [id]);

  useEffect(() => {
    const loadReasons = async () => {
      try {
        const res = await getSalesReturnReason();
        if (res.status === "success") {
          setReasonList(res.data);
        }
      } catch (error) {
        console.error("Failed to fetch reason list", error);
      }
    };
    loadReasons();
  }, []);

  if (!orderData) return null;

  const calculateActualQty = (item: Order["items"][0]) => {
    const updatedQtyArr = item.updatedQuantity ?? [];
    const updatedQty = updatedQtyArr.length > 0 ? updatedQtyArr[0].quantity : Number(item.quantity);

    const returnedQuantity = orderData?.salesReturns?.items
      ?.filter((sr: any) => sr.product_id === item.product_id)
      .reduce((acc: number, sr: any) => acc + Number(sr.quantity || 0), 0) ?? 0;

    const actualQty = updatedQty - returnedQuantity;
    return actualQty > 0 ? actualQty : 0;
  };

  const handleSelectProduct = (productId: number) => {
    if (!orderData) return;
    const product = orderData.items.find((i) => i.product_id === productId);
    if (!product) return;

    if (selectedProducts.some((p) => p.product_id === product.product_id)) {
      toast.warning("Product already added");
      return;
    }

    const actualQty = calculateActualQty(product);
    if (actualQty === 0) {
      toast.warning("Cannot add product with zero actual quantity");
      return;
    }

    setSelectedProducts((prev) => [
      ...prev,
      {
        product_id: product.product_id,
        name: product.product.name,
        quantity: actualQty,
        maxQuantity: actualQty,
        reasonId: "",
        files: [],
      },
    ]);
    setSelectedProductId("");
  };

  const handleQuantityChange = (product_id: number, value: string) => {
    if (value === "" || !/^[0-9\b]+$/.test(value)) return;
    const newValue = parseInt(value, 10);

    setSelectedProducts((prev) =>
      prev.map((p) => {
        if (p.product_id === product_id) {
          let qty = newValue;
          if (qty < 1) qty = 1;
          else if (qty > p.maxQuantity) qty = p.maxQuantity;
          return { ...p, quantity: qty };
        }
        return p;
      })
    );
  };

  const handleReasonChange = (product_id: number, value: string | number) => {
    setSelectedProducts((prev) =>
      prev.map((p) =>
        p.product_id === product_id
          ? { ...p, reasonId: value === "" ? "" : Number(value) }
          : p
      )
    );
  };

  const handleFileChange = (product_id: number, files: FileList | null) => {
    if (!files) return;
    const fileArray = Array.from(files);
    setSelectedProducts((prev) =>
      prev.map((p) =>
        p.product_id === product_id ? { ...p, files: fileArray } : p
      )
    );
  };

  // Updated unified submit handler
  const handleSubmitRow = async () => {
    if (selectedProducts.length === 0) {
      toast.error("Please select at least one product to return");
      return;
    }

    // Validate all products
    for (const product of selectedProducts) {
      if (!product.reasonId) {
        toast.error(`Please select a reason for ${product.name}`);
        return;
      }
    }

    // Build structured FormData
    const formData = new FormData();
    formData.append("orderId", String(orderData!.id));
    if (notes) formData.append("notes", notes);

    selectedProducts.forEach((p, i) => {
      formData.append(`products[${i}][returnProductId]`, String(p.product_id));
      formData.append(`products[${i}][quantity]`, String(p.quantity));
      formData.append(`products[${i}][reasonId]`, String(p.reasonId));
      if (p.files && p.files.length > 0) {
        p.files.forEach((file) => {
          formData.append(`products[${i}][images]`, file);
        });
      }
    });

    try {
      setSubmitting(true);
      const res = await createSalesReturn(formData);
      if (res.data.status === "success") {
        toast.success(res.data.message);
        setSelectedProducts([]); // clear after success
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error("Failed to create sales return");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={5}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h5" fontWeight={600} mb={2}>
        Sales Return
      </Typography>

      {/* Product Dropdown */}
      <Box display="flex" gap={2} mb={3}>
        <Select
          value={selectedProductId}
          onChange={(e) => handleSelectProduct(Number(e.target.value))}
          displayEmpty
          sx={{ minWidth: 250 }}
        >
          <MenuItem value="">Select Product</MenuItem>
          {orderData.items.map((item) => {
            const actualQty = calculateActualQty(item);
            return (
              <MenuItem
                key={item.product_id}
                value={item.product_id}
                disabled={
                  actualQty === 0 ||
                  selectedProducts.some((p) => p.product_id === item.product_id)
                }
              >
                {item.product.name} (Qty: {actualQty})
              </MenuItem>
            );
          })}
        </Select>
      </Box>

      {/* Selected Products Table */}
      {selectedProducts.length > 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Product Name</strong></TableCell>
                <TableCell><strong>Quantity</strong></TableCell>
                <TableCell><strong>Reason</strong></TableCell>
                <TableCell><strong>Files</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedProducts.map((p) => (
                <TableRow key={p.product_id}>
                  <TableCell>{p.name}</TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={p.quantity}
                      inputProps={{ min: 1, max: p.maxQuantity }}
                      onChange={(e) =>
                        handleQuantityChange(p.product_id, e.target.value)
                      }
                      sx={{ width: 80 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      displayEmpty
                      size="small"
                      value={p.reasonId ?? ""}
                      onChange={(e) =>
                        handleReasonChange(p.product_id, e.target.value)
                      }
                      sx={{ minWidth: 180 }}
                    >
                      <MenuItem value="">Select Reason</MenuItem>
                      {reasonList.map((reason) => (
                        <MenuItem key={reason.id} value={reason.id}>
                          {reason.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell>
                    <input
                      type="file"
                      multiple
                      onChange={(e) =>
                        handleFileChange(p.product_id, e.target.files)
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}

              <TableRow>
                <TableCell colSpan={4}>
                  <TextField
                    label="Notes"
                    size="small"
                    fullWidth
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell colSpan={4} align="right">
                  <button
                    style={{
                      backgroundColor: "#1976d2",
                      color: "#fff",
                      padding: "8px 16px",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      marginTop: "10px",
                      opacity: submitting ? 0.7 : 1,
                    }}
                    onClick={handleSubmitRow}
                    disabled={selectedProducts.length === 0 || submitting}
                  >
                    {submitting ? "Submitting..." : "Submit All"}
                  </button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default CreateSalesReturn;
